import React, { useState, useEffect } from 'react';

import { IVideoModuleFields } from '@/types/contentful';

import { useUserContext } from '../../context/User';
import { trackWithFingerprint } from '../../utils/trackWithFingerprint';
import { getPartnerTrackingProperties } from '../../utils/getPartnerTrackingProperties';
import { captureError, trackRumEvent } from '../../utils/datadogRum';

import { Lightbox } from '../VideoBlock/Lightbox';

import cx from 'classnames';
import { useVideo } from '~/hooks/useVideo';

const VIDEO_ERRORS_TO_IGNORE = [
  'play() request was interrupted by a call to pause',
  'play() request was interrupted by a new load request'
];

interface VideoPlayerProps extends IVideoModuleFields {
  rounded?: boolean;
  fullwidth?: boolean;
  variant?: 'default' | 'gl4c';
  sysId?: string;
}

export function VideoPlayer({
  title,
  vimeoId,
  loop,
  mp4video,
  fallbackImage,
  rounded,
  fullwidth = true,
  sysId
}: VideoPlayerProps) {
  const { educator } = useUserContext();
  const [showingVideo, setShowingVideo] = useState(false);
  const { videoRef, sourceUrl } = useVideo(mp4video?.fields?.file?.url, {
    manageVisibility: true,
    shouldPlay: true,
    intersectionThreshold: 0.25,
    deferUntilVisible: true
  });

  useEffect(() => {
    videoRef.current?.play()?.catch((err: DOMException) => {
      if (!VIDEO_ERRORS_TO_IGNORE.includes(err.message)) {
        trackRumEvent('autoplay_failed', {
          page_url: window.location.href,
          ...educator,
          ...getPartnerTrackingProperties()
        });
        captureError(err);
      }
    });
  }, []);

  const toggleVideo = (displayVideo: boolean) => {
    setShowingVideo(displayVideo);
    if (displayVideo) {
      trackWithFingerprint('Video Opened', {
        page_url: window.location.href,
        ...educator,
        ...getPartnerTrackingProperties()
      });
    } else {
      trackWithFingerprint('Video Closed', {
        page_url: window.location.href,
        ...educator,
        ...getPartnerTrackingProperties()
      });
      document.getElementById('gl-video-play-button')?.focus();
    }
  };

  const trackVideoPlay = () => {
    trackWithFingerprint('Video Playing', {
      page_url: window.location.href,
      video_title: title,
      ...educator,
      ...getPartnerTrackingProperties()
    });
  };

  return (
    <div
      className={cx(
        'mx-auto relative w-full flex justify-center items-center overflow-hidden tablet:w-full tablet:h-full',
        {
          'min-[320px]:w-screen min-[320px]:h-screen': fullwidth,
          'min-[320px]:w-full min-[320px]:h-full': !fullwidth,
          'rounded-4xl min-[320px]:rounded-2xl tablet:rounded-4xl': rounded,
          'rounded-none min-[320px]:rounded-none tablet:rounded-none': !rounded
        }
      )}
      data-blockid="videoModuleWrapper"
      id={sysId}
    >
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        loop={loop!}
        poster={fallbackImage?.fields?.file?.url}
        playsInline
        muted
        controls={false}
        preload="metadata"
        crossOrigin="anonymous"
        onPlay={trackVideoPlay}
      >
        <track kind="captions" />
        <source src={sourceUrl} />
        Your browser does not support HTML video.
      </video>
      <button
        id="gl-video-play-button"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          toggleVideo(true);
          e.currentTarget.blur();
        }}
        aria-label="Play video"
        data-testid="videomodule-button"
        className="z-[1] outline-0 absolute w-[3.625rem] h-[3.625rem] tablet:w-20 tablet:h-20 border-0 appearance-none -webkit-appearance-none p-0 m-0 cursor-pointer bg-transparent [&_.playIconBackground]:transition-[fill_0.2s_ease] [&_.playIconCenter]:transition-[fill_0.2s_ease] hover:[&_.playIconBackground]:fill-[#80D6B0] focus:[&_.playIconBackground]:fill-[#80D6B0] hover:[&_.playIconCenter]:fill-[#035C67] focus:[&_.playIconCenter]:fill-[#035C67]"
      >
        <svg
          width="80"
          height="80"
          viewBox="0 0 100 100"
          role="img"
          data-testid="assetWith5050Text-video"
          className="cursor-pointer max-w-full z-0 m-auto"
        >
          <path
            className="playIconBackground"
            opacity={0.8}
            d="M0 24C0 10.745 10.745 0 24 0h32c13.255 0 24 10.745 24 24v32c0 13.255-10.745 24-24 24H24C10.745 80 0 69.255 0 56V24z"
            fill="#1D252C"
          />
          <path
            className="playIconCenter"
            d="M49.666 37.935c1.333.77 1.333 2.694 0 3.464l-13.5 7.794c-1.333.77-3-.192-3-1.732V31.873c0-1.54 1.667-2.502 3-1.733l13.5 7.795z"
            fill="#3BBFAD"
          />
        </svg>
      </button>

      {vimeoId && title && (
        <Lightbox
          title={title}
          showingVideo={showingVideo}
          setShowingVideo={toggleVideo}
          vimeoId={vimeoId}
        />
      )}
    </div>
  );
}
